import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Autocomplete, TextField, Stack, Typography, Select, MenuItem, FormControl, InputLabel, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useColors } from 'legacyfrontend/hooks/useColors';
import HotelDestinationDialog from './HotelDestinationDialog';
import DatePickerFullScreenDialog from './DatePickerFullScreenDialog';
import DateRangeSelector from './DateRangeSelector';
import HabitacionesSelector from './HabitacionesSelector';
import { COUNTRIES } from 'legacyfrontend/constants/countries';

const HotelFilterForm = ({
  destinations,
  handleDestinationSelect,
  handleDestinationSearch,
  handleDateRangeSelect,
  handleOccupancyChange,
  handleResidenceCountryChange,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isDatesEmpty,
  setIsDatesEmpty,
  onMobileSearchComplete,
  isMobileSearchActive = false,
}) => {
  const { t } = useTranslation('layout');
  const { background } = useColors();

  const [selectedDestination, setSelectedDestination] = useState(null);
  const [checkInDate, setCheckInDate] = useState(formInput?.checkInDate || formInput?.departureDate || null);
  const [checkOutDate, setCheckOutDate] = useState(formInput?.checkOutDate || formInput?.returnDate || null);

  const [rooms, setRooms] = useState(formInput?.rooms || 1);
  const [adultsPerRoom, setAdultsPerRoom] = useState(formInput?.adultsPerRoom || [2]);
  const [childrenPerRoom, setChildrenPerRoom] = useState(formInput?.childrenPerRoom || [0]);
  const [childrenAges, setChildrenAges] = useState(formInput?.childrenAges || [[]]);
  const [residenceCountry, setResidenceCountry] = useState(formInput?.residenceCountry || '');

  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const destinationInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current && formInput) {
      isInitialMount.current = false;
      if (formInput?.checkInDate || formInput?.departureDate) {
        setCheckInDate(formInput.checkInDate || formInput.departureDate);
      }
      if (formInput?.checkOutDate || formInput?.returnDate) {
        setCheckOutDate(formInput.checkOutDate || formInput.returnDate);
      }
      if (formInput?.rooms) setRooms(formInput.rooms);
      if (formInput?.adultsPerRoom) setAdultsPerRoom(formInput.adultsPerRoom);
      if (formInput?.childrenPerRoom) setChildrenPerRoom(formInput.childrenPerRoom);
      if (formInput?.childrenAges) setChildrenAges(formInput.childrenAges);
      if (formInput?.residenceCountry) setResidenceCountry(formInput.residenceCountry);
    }
  }, []);

  const handleDestinationChange = useCallback((value) => {
    if (selectedDestination?.id === value?.id && selectedDestination?.cityCode === value?.cityCode) {
      if (value) {
        setIsDestinationDialogOpen(false);
      }
      return;
    }

    setSelectedDestination(value);
    handleDestinationSelect(value);
    setIsDestinationEmpty(false);
    if (value) {
      setIsDestinationDialogOpen(false);
    }
  }, [handleDestinationSelect, setIsDestinationEmpty, selectedDestination]);

  const handleDateChange = useCallback(({ startDate, endDate }) => {
    setCheckInDate(startDate);
    setCheckOutDate(endDate);
    handleDateRangeSelect(startDate, endDate);
    setIsDatesEmpty(false);
  }, [handleDateRangeSelect, setIsDatesEmpty]);

  const handleRoomsChange = useCallback((newRooms) => {
    const numRooms = Math.max(1, Math.min(newRooms, 5));
    setRooms(numRooms);

    const newAdults = [...adultsPerRoom];
    const newChildren = [...childrenPerRoom];
    const newAges = [...childrenAges];

    while (newAdults.length < numRooms) {
      newAdults.push(2);
      newChildren.push(0);
      newAges.push([]);
    }
    while (newAdults.length > numRooms) {
      newAdults.pop();
      newChildren.pop();
      newAges.pop();
    }

    setAdultsPerRoom(newAdults);
    setChildrenPerRoom(newChildren);
    setChildrenAges(newAges);
  }, [adultsPerRoom, childrenPerRoom, childrenAges]);

  const handleAdultsChange = useCallback((roomIndex, newValue) => {
    const newAdults = [...adultsPerRoom];
    newAdults[roomIndex] = Math.max(1, Math.min(newValue, 4));
    setAdultsPerRoom(newAdults);
  }, [adultsPerRoom]);

  const handleChildrenChange = useCallback((roomIndex, newValue) => {
    const newChildren = [...childrenPerRoom];
    newChildren[roomIndex] = Math.max(0, Math.min(newValue, 4 - adultsPerRoom[roomIndex]));
    setChildrenPerRoom(newChildren);

    const newAges = [...childrenAges];
    while (newAges[roomIndex].length < newValue) {
      newAges[roomIndex].push(2);
    }
    while (newAges[roomIndex].length > newValue) {
      newAges[roomIndex].pop();
    }
    setChildrenAges(newAges);
  }, [adultsPerRoom, childrenPerRoom, childrenAges]);

  const handleChildAgeChange = useCallback((roomIndex, childIndex, newAge) => {
    const newAges = [...childrenAges];
    if (!newAges[roomIndex]) {
      newAges[roomIndex] = [];
    }
    newAges[roomIndex][childIndex] = Math.max(0, Math.min(newAge, 17));
    setChildrenAges(newAges);
  }, [childrenAges]);

  const handleResidenceCountrySelect = useCallback((countryCode) => {
    if (residenceCountry === countryCode) {
      return;
    }

    setResidenceCountry(countryCode);
    handleResidenceCountryChange(countryCode);
  }, [handleResidenceCountryChange, residenceCountry]);

  useEffect(() => {
    const totalAdults = adultsPerRoom.reduce((sum, adults) => sum + adults, 0);
    const totalChildren = childrenPerRoom.reduce((sum, children) => sum + children, 0);

    handleOccupancyChange({
      rooms,
      adultsPerRoom,
      childrenPerRoom,
      childrenAges,
      totalAdults,
      totalChildren,
    });
  }, [rooms, adultsPerRoom, childrenPerRoom, childrenAges, handleOccupancyChange]);

  const getDestinationDisplay = useMemo(() => {
    if (!selectedDestination) return 'Destino';
    if (selectedDestination.cityName) return selectedDestination.cityName;
    if (selectedDestination.name) return selectedDestination.name;
    return 'Destino';
  }, [selectedDestination]);

  const getDateDisplay = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 'Check-in - Check-out';
    const formatDate = (date) => {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    return `${formatDate(checkInDate)} - ${formatDate(checkOutDate)}`;
  }, [checkInDate, checkOutDate]);

  const sortedDestinations = useMemo(() => {
    if (!destinations?.data) return [];

    return [...destinations.data].sort((a, b) => {
      const countryA = a.countryName || '';
      const countryB = b.countryName || '';
      const cityA = a.cityName || a.name || '';
      const cityB = b.cityName || b.name || '';

      if (countryA < countryB) return -1;
      if (countryA > countryB) return 1;
      if (cityA < cityB) return -1;
      if (cityA > cityB) return 1;
      return 0;
    });
  }, [destinations?.data]);

  const ListboxProps = useMemo(
    () => ({
      style: {
        maxHeight: '300px',
      },
    }),
    []
  );

  const PopperComponent = useCallback(
    (props) => <Popper {...props} style={{ width: 'fit-content' }} placement='bottom-start' />,
    []
  );

  const renderGroup = useCallback(
    (params) => (
      <li key={params.key}>
        <div
          style={{
            position: 'sticky',
            top: 0,
            padding: '8px 10px',
            color: '#000',
            backgroundColor: '#fafafa',
            fontWeight: 600,
            fontSize: '12px',
            zIndex: 1,
          }}
        >
          {params.group}
        </div>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    []
  );

  if (isMobile) {
    return (
      <>
        <Stack spacing={2}>
          <Stack>
            <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
              Destino
            </Typography>
            <div
              onClick={() => setIsDestinationDialogOpen(true)}
              style={{
                padding: '12px',
                border: isDestinationEmpty ? '2px solid red' : '1px solid #ccc',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography>{getDestinationDisplay}</Typography>
            </div>
          </Stack>

          <Stack>
            <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
              Fechas
            </Typography>
            <div
              onClick={() => setIsDatePickerOpen(true)}
              style={{
                padding: '12px',
                border: isDatesEmpty ? '2px solid red' : '1px solid #ccc',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <Typography>{getDateDisplay}</Typography>
            </div>
          </Stack>

          <Stack>
            <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
              Habitaciones
            </Typography>
            <HabitacionesSelector
              rooms={rooms}
              setRooms={setRooms}
              adultsPerRoom={adultsPerRoom}
              setAdultsPerRoom={setAdultsPerRoom}
              childrenPerRoom={childrenPerRoom}
              setChildrenPerRoom={setChildrenPerRoom}
              childrenAges={childrenAges}
              setChildrenAges={setChildrenAges}
            />
          </Stack>

          <Stack>
            <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
              País de residencia
            </Typography>
            <FormControl fullWidth>
              <Select
                value={residenceCountry}
                onChange={(e) => handleResidenceCountrySelect(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '30vh',
                    },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    color: residenceCountry ? '#000' : '#aaa',
                    fontSize: '16px',
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ color: '#aaa', fontSize: '16px' }}>
                  Seleccione un país
                </MenuItem>
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code} sx={{ fontSize: '16px' }}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {isDestinationDialogOpen && (
          <HotelDestinationDialog
            options={sortedDestinations}
            onSelect={handleDestinationChange}
            selectedDestination={selectedDestination}
            isOpen={isDestinationDialogOpen}
            setIsOpen={setIsDestinationDialogOpen}
            isDestinationEmpty={isDestinationEmpty}
            setIsDestinationEmpty={setIsDestinationEmpty}
            handleDestinationSearch={handleDestinationSearch}
            isLoading={destinations?.isLoading}
          />
        )}

        {isDatePickerOpen && (
          <DatePickerFullScreenDialog
            startDate={checkInDate}
            endDate={checkOutDate}
            isOpen={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            isDatesEmpty={isDatesEmpty}
            onChange={handleDateChange}
            isOnlyOneWay={false}
            handleSwitchChange={() => { }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'flex-end' }}>
        <Stack sx={{ flex: 2 }}>
          <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
            Destino
          </Typography>
          <Autocomplete
            ListboxProps={ListboxProps}
            PopperComponent={PopperComponent}
            className={`form__field destination-typeahead ${isDestinationEmpty ? 'autocomplete-error' : ''}`}
            clearOnBlur
            getOptionLabel={(option) =>
              `${option.cityName || option.name}`
            }
            groupBy={(option) => option.countryName || 'Otros'}
            id='destination-autocomplete'
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return option.id === value.id;
            }}
            loading={destinations?.isLoading}
            loadingText='Cargando destinos'
            noOptionsText='No hay resultados'
            onChange={(_, newValue) => handleDestinationChange(newValue)}
            onFocus={() => setIsDestinationEmpty(false)}
            onInputChange={(_, value) => handleDestinationSearch && handleDestinationSearch(value)}
            options={sortedDestinations}
            renderGroup={renderGroup}
            renderOption={(props, option, state) => {
              const key = `${option.id}-${option.cityCode}-${option.countryName || 'other'}-${state.index}`;
              return (
                <li {...props} key={key}>
                  {option.cityName || option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
                inputRef={destinationInputRef}
                placeholder="Destino"
                onFocus={() => {
                  const input = document.querySelector('.destination-typeahead');
                  if (input) {
                    const closeBtn = input.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleDestinationSelect(undefined);
                }}
              />
            )}
            size='small'
            style={{ width: '100%' }}
            value={selectedDestination}
          />
        </Stack>

        <Stack sx={{ flex: 2 }}>
          <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
            Fechas
          </Typography>
          <DateRangeSelector
            startDate={checkInDate}
            endDate={checkOutDate}
            onChange={handleDateChange}
            error={isDatesEmpty}
            helperText={isDatesEmpty ? 'Seleccione las fechas' : ''}
            disabled={false}
            minDate={new Date()}
            isOneWay={false}
            startDateLabel='Check-in'
            endDateLabel='Check-out'
          />
        </Stack>

        <Stack sx={{ flex: 1 }}>
          <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
            Habitaciones
          </Typography>
          <HabitacionesSelector
            rooms={rooms}
            setRooms={setRooms}
            adultsPerRoom={adultsPerRoom}
            setAdultsPerRoom={setAdultsPerRoom}
            childrenPerRoom={childrenPerRoom}
            setChildrenPerRoom={setChildrenPerRoom}
            childrenAges={childrenAges}
            setChildrenAges={setChildrenAges}
          />
        </Stack>

        {/* País de residencia */}
        <Stack sx={{ flex: 1.5 }}>
          <Typography color='#4f4f4f' fontSize={12} mb={0.5}>
            País de residencia
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={residenceCountry}
              onChange={(e) => handleResidenceCountrySelect(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '30vh',
                  },
                },
              }}
              sx={{
                '& .MuiSelect-select': {
                  color: residenceCountry ? '#000' : '#aaa',
                  fontSize: '16px',
                },
              }}
            >
              <MenuItem value="" disabled sx={{ color: '#aaa', fontSize: '16px' }}>
                Seleccione un país
              </MenuItem>
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code} sx={{ fontSize: '16px' }}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </>
  );
};

export default HotelFilterForm;

